import { z } from 'zod/v4';

export const sendpulseProxyErrorSchema = z.object({
    result: z.literal(false),
    error: z.string(),
    warning: z.string().nullable().optional(),
    balance: z.number().optional(),
    custom_key_failed: z.boolean().optional(),
});

export type SendpulseProxyError = z.infer<typeof sendpulseProxyErrorSchema>;

export type AiChatErrorText = { key: string; params?: Record<string, unknown> };

const AI_CHAT_ERROR_KEY_BY_STATUS: Readonly<Record<number, string>> = {
    405: 'ai_chat_error_unavailable',
};

const AI_CHAT_ERROR_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    insufficient_credits: 'ai_chat_error_no_credits',
    estimate_exceeds_balance: 'ai_chat_error_estimate_exceeds_balance',
    user_not_found: 'ai_chat_error_no_plan',
    subscription_expired: 'ai_chat_error_subscription_expired',
    model_not_available: 'ai_chat_error_misconfiguration',
    unknown: 'ai_chat_error_credits_unknown',
    custom_key_rejected: 'ai_chat_error_custom_key_rejected',
    'Provider key unavailable': 'ai_chat_error_provider_unavailable',
};

const NEEDS_BALANCE = new Set(['ai_chat_error_no_credits', 'ai_chat_error_estimate_exceeds_balance']);

const ACCOUNTING_UNREACHABLE_PATTERN = /^AI accounting (?:unavailable|unreachable)\b/;

const PROVIDER_KEY_REJECTED_PATTERN = /^(.+) returned HTTP \d+$/;

const PROVIDER_UNREACHABLE_PATTERN = /^(.+) provider unreachable$/;

function textForErrorValue(error: SendpulseProxyError): AiChatErrorText | null {
    const key = AI_CHAT_ERROR_KEY_BY_VALUE[error.error];

    if (key) {
        return NEEDS_BALANCE.has(key) ? { key, params: { balance: error.balance ?? 0 } } : { key };
    }

    if (ACCOUNTING_UNREACHABLE_PATTERN.test(error.error)) {
        return { key: 'ai_chat_error_accounting_unavailable' };
    }

    const keyRejected = PROVIDER_KEY_REJECTED_PATTERN.exec(error.error);

    if (keyRejected) {
        return { key: 'ai_chat_error_provider_key_rejected', params: { provider: keyRejected[1] } };
    }

    const unreachable = PROVIDER_UNREACHABLE_PATTERN.exec(error.error);

    return unreachable ? { key: 'ai_chat_error_provider_unreachable', params: { provider: unreachable[1] } } : null;
}

function isRetryableStatus(status: number): boolean {
    return status === 408 || status === 409 || status === 429 || status >= 500;
}

export function sendpulseProxyErrorToText(status: number, error: SendpulseProxyError | null): AiChatErrorText | null {
    const specific = error && textForErrorValue(error);

    if (specific) {
        return specific;
    }

    const explained = AI_CHAT_ERROR_KEY_BY_STATUS[status];

    if (explained) {
        return { key: explained, params: { balance: error?.balance ?? 0, status } };
    }

    if (error) {
        return { key: 'ai_chat_error_detail', params: { detail: error.warning ?? error.error } };
    }

    return isRetryableStatus(status) ? null : { key: 'ai_chat_error_generic', params: { status } };
}
